import React from "react";
import { StyleSheet, View, Image, Text, PixelRatio } from "react-native";
import { Icons } from "../../assets";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import { useGlobalState } from "../../shared/state";
import { I18N } from "../../shared/util";
import TextTitle from "../../components/Text/TextTitle";
import SquareButton from "../../components/Buttons/generic-button/SquareButton";
import I18n from "../../i18nSets/i18n";
import { getSizeScaleCategoriesAndMarker } from "../../shared/products";

type ShoeSizeScaleProps = {
  value?: number | string;
  secondaryValue?: number | string;
};

const ShoeSizeScale = ({ value, secondaryValue }: ShoeSizeScaleProps) => {
  const toNumber = (v: number | string | undefined) => {
    if (v === undefined || (typeof v === "string" && v.trim() === "")) return undefined;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const center = toNumber(value);
  const secondary = toNumber(secondaryValue);

  // Hard stop if required value is invalid
  if (center === undefined) {
    return <View style={styles.scalesContainer}><Text style={styles.emptySize}>-</Text></View>;
  }

  const { categories, markerIndex } = getSizeScaleCategoriesAndMarker(center, secondary);
  // Add an intermediate tick between each shared category position.
  const ticks = Array.from({ length: categories.length * 2 - 1 }, (_, i) => i);

  return (
    <View style={styles.scalesContainer}>
      {/* Labels */}
      <View style={styles.labels}>
        {ticks.map((i) => (
          <View key={i} style={styles.tickWrapper}>
            <Text style={styles.label}>{i % 2 === 0 ? categories[i / 2].name : ""}</Text>
          </View>
        ))}
      </View>

      {/* Scale */}
      <View style={styles.scale}>
        {ticks.map((i) => {
          const isMarker = i === markerIndex * 2;
          return (
            <View key={i} style={styles.tickWrapper}>
              <View style={[styles.tick, styles.smallTick, isMarker && styles.primaryTick]} />
            </View>
          );
        })}
      </View>
    </View>
  );
};

const BirkenstockSizeRecommendation = () => {
  const [currentScanner] = useGlobalState("currentScanner");
  const [language] = useGlobalState("language");
  const [birkenstockSummary] = useGlobalState("birkenstockSummary");
  const sandal = birkenstockSummary?.sandal;
  const shoe = birkenstockSummary?.shoe;
  const alternativeRecommendations = [
    { category: "SANDALS / CLOGS", recommendation: sandal },
    { category: "SHOES", recommendation: shoe },
  ].filter(({ recommendation }) => recommendation?.width?.toLowerCase() === "regular" && recommendation.narrowAlternative);

  const i18n = I18N(language);

  return (
    <LayoutWrapper
      showHeaderCloseButton={false}
      showHeaderLogo={false}
      title={i18n.t("sizeRecommendation").toLocaleUpperCase()}
      hideInfoButton={true}
      showBirkenstockFooterLogo={true}
      showBirkenstockFooter={true}
    >
      <View style={styles.contentWrapper}>
        <View style={styles.mainContainer}>
          <View style={styles.cardsRow}>
            {/* SANDALS */}
            <View style={styles.card}>
              <Image source={Icons.sandalRecommendationIcon} style={styles.topIcon} />

              <View style={styles.headerRow}>
                <TextTitle text={"SANDALS / CLOGS"} fontSize={26} color="#0e2288" fontFamily="Birkentype" />
                <TextTitle text={sandal?.width || "-"} fontWeight={"bold"} fontSize={20} color="#0e2288" fontFamily="Geist-Light" />
              </View>

              <View style={styles.bottomRow}>
                <ShoeSizeScale value={sandal?.size} secondaryValue={sandal?.secondarySize} />
                {!!sandal?.width && <Image source={sandal.width.toLowerCase() === "regular" ? Icons.regularFootCategoryIcon : Icons.narrowFootCategoryIcon} style={styles.footIcon} />}
              </View>
            </View>

            {/* SHOES */}
            <View style={styles.card}>
              <Image source={Icons.shoeRecommendationIcon} style={styles.topIcon} />

              <View style={styles.headerRow}>
                <TextTitle text={"SHOES"} fontSize={26} color="#0e2288" fontFamily="Birkentype" />
                <TextTitle text={shoe?.width || "-"} fontWeight={"bold"} fontSize={20} color="#0e2288" fontFamily="Geist-Light" />
              </View>

              <View style={styles.bottomRow}>
                <ShoeSizeScale value={shoe?.size} secondaryValue={shoe?.secondarySize} />
                {!!shoe?.width && <Image source={shoe.width.toLowerCase() === "regular" ? Icons.regularFootCategoryIcon : Icons.narrowFootCategoryIcon} style={styles.footIcon} />}
              </View>
            </View>
          </View>

          {alternativeRecommendations.map(({ category, recommendation }) => (
            <TextTitle
              key={category}
              text={`${category}: ${i18n.t("scan.secondarySizeRecommendationBirkenstock")} ${recommendation.narrowAlternative.size} ${recommendation.narrowAlternative.width}`}
              fontSize={22}
              color="#0e2288"
              fontFamily="Geist-Light"
            />
          ))}
        </View>

        <View style={styles.footer}>
          <SquareButton
            text={I18n.t("next", { locale: language })}
            color="#0e2288"
            navigateTo="BirkenstockPrintScreen"
            navigateOptions={{ resetState: true }}
            deactivated={currentScanner === undefined}
            width={337}
            height={77}
            textColor="white"
            fontSize={36}
          />
        </View>
      </View>
    </LayoutWrapper>
  );
};

export default BirkenstockSizeRecommendation;

const styles = StyleSheet.create({
  contentWrapper: {
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
  },

  mainContainer: {
    width: "65%",
    height: "75%",
    alignSelf: "center",
    flexDirection: "column",
  },

  cardsRow: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center",
  },

  card: {
    width: "45%",
    height: "75%",
    flexDirection: "column",
    justifyContent: "space-evenly",
    backgroundColor: "white",
    shadowColor: "#000",
    shadowOffset: { width: 6 / PixelRatio.get(), height: 6 / PixelRatio.get() },
    shadowOpacity: 0.16,
    shadowRadius: 4 / PixelRatio.get(),
    elevation: 1,
  },

  topIcon: {
    width: "30%",
    height: "25%",
    resizeMode: "contain",
    alignSelf: "center",
  },

  headerRow: {
    width: "90%",
    height: "15%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingLeft: "5%",
  },

  bottomRow: {
    width: "100%",
    height: "35%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: "5%",
  },

  footIcon: {
    width: "30%",
    height: "100%",
    resizeMode: "contain",
    alignSelf: "center",
  },

  footer: {
    width: "20%",
    minHeight: "10%",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },

  /* existing unused styles kept as-is */
  scalesContainer: {
    backgroundColor: "#0A1E8A",
    paddingBottom: "1%",
    paddingHorizontal: "1.5%",
    width: "60%",
    height: "70%",
  },
  labels: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: 28,
    marginBottom: 10,
  },
  label: {
    color: "white",
    fontSize: 22,
    fontWeight: "bold",
    textAlign: "center",
    width: 48,
  },
  emptySize: {
    color: "white",
    fontSize: 22,
    textAlign: "center",
  },
  scale: {
    flexDirection: "row",
    alignItems: "flex-end",
    height: 26,
  },
  tickWrapper: {
    flex: 1,
    alignItems: "center",
  },
  tick: {
    backgroundColor: "white",
    borderRadius: 1,
  },
  smallTick: {
    width: 2,
    height: 14,
    opacity: 0.6,
  },
  primaryTick: {
    width: 6,
    height: 26,
    borderRadius: 3,
    opacity: 1,
  },
});
